import React from 'react';

import { Box, Stack, Typography } from '@mui/material';

import type { ChatTraceEntry, ChatTraceExecutionRecord } from '~/services/TripPlannerService';

import ChatReviewContent from './ChatReviewContent';
import ChatTraceToolCall from './ChatTraceToolCall';
import { formatTraceDuration } from './chatTraceTiming';

interface Props {
  entry: ChatTraceEntry;
  renderContent?: boolean;
  records?: ChatTraceExecutionRecord[];
  linkAction?: React.ReactNode;
}

export default function ChatTraceEntry({ entry, renderContent = false, records = [], linkAction }: Props) {
  const identifier = 'messageId' in entry ? (entry.messageId ?? entry.id) : entry.id;
  const models = records.filter((record) => record.kind === 'model' && record.messageId === identifier);
  const agentStep = 'toolExecutions' in entry ? entry : null;
  return (
    <Box
      component="section"
      sx={{ p: { xs: 2, md: 2.5 }, borderRadius: 2, bgcolor: entry.role === 'user' ? 'grey.100' : 'background.paper' }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="baseline"
        flexWrap="wrap"
        gap={1}
        sx={{ mb: 1.5 }}
      >
        <Stack direction="row" alignItems="baseline" flexWrap="wrap" gap={1.5} sx={{ minWidth: 0 }}>
          <Typography variant="subtitle2">{entry.role === 'user' ? 'User' : 'Assistant'}</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
            {identifier}
          </Typography>
        </Stack>
        <Stack direction="row" alignItems="center" flexWrap="wrap" gap={2} color="text.secondary">
          {models.map((model) => (
            <Typography key={model.id} variant="caption">
              Model: {formatTraceDuration(model.durationMs)} · {model.status}
            </Typography>
          ))}
          {agentStep?.inputTokens != null && (
            <Typography variant="caption">Input tokens: {agentStep.inputTokens.toLocaleString()}</Typography>
          )}
          {agentStep?.outputTokens != null && (
            <Typography variant="caption">Output tokens: {agentStep.outputTokens.toLocaleString()}</Typography>
          )}
          {linkAction}
        </Stack>
      </Stack>
      <Stack spacing={2.5}>
        <Box>
          {entry.content &&
            (renderContent ? (
              <ChatReviewContent content={entry.content} />
            ) : (
              <Typography
                component="div"
                variant="body2"
                sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', lineHeight: 1.8 }}
              >
                {entry.content}
              </Typography>
            ))}
        </Box>
        {agentStep?.toolExecutions.map((execution, index) => (
          <ChatTraceToolCall
            key={execution.call.id ?? index}
            execution={execution}
            timings={records.filter(
              (record) => record.kind === 'tool' && record.toolCallId && record.toolCallId === execution.call.id,
            )}
          />
        ))}
      </Stack>
    </Box>
  );
}
